import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { isEqual, isNil } from 'lodash-es';

import { useMediaQuery } from '@/components/utils/useQueryMedia';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import PageLoader from '@/components/page-loader';
import { useAppDispatch, useAppSelector } from '@/store';
import { clearRecommendedCampaigns, sessionFetchRecommendedCampaigns, sessionSave } from '@/store/slices/session';

import { resetMarketScanAnimation, setMarketScanAnimationFinished } from '../market-scan/hooks';

import {
  CONFIGURATOR_DEFAULT_CURRENCY,
  CONFIGURATOR_DESIGN_STEP,
  CONFIGURATOR_ENGAGEMENT_STEP,
  CONFIGURATOR_MOBILE_MEDIA_QUERY,
} from './constants';
import {
  useBreakpoints,
  useConfigFetch,
  useCountry,
  useDesignStep,
  useEngagementStep,
  useFullscreen,
  useIframe,
  useSession,
  useSteps,
  useTrafficStep,
} from './hooks';
import { ConfiguratorContextValue, ConfiguratorProps, SetSessionCallback } from './types';
import { splitPrices } from './utils';

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const SparkConfigurator: React.FC<ConfiguratorProps> = (componentProps) => {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const session = useAppSelector((state) => state.session, isEqual);
  const authentication = useAppSelector((state) => state.authentication, isEqual);

  const isMobile = useMediaQuery(CONFIGURATOR_MOBILE_MEDIA_QUERY);

  const setSession = React.useCallback<SetSessionCallback>(
    (partialSessionData) => {
      if (isNil(session.sessionId) || !session.loaded) {
        return;
      }

      dispatch(
        sessionSave({
          id: session.sessionId,
          data: {
            ...session.data,
            configuration_data: {
              ...session.data.configuration_data,
              emailAddress: authentication.user.email,
              ...partialSessionData,
            },
          },
        }),
      );
    },
    [dispatch, authentication.user.email, session.data, session.loaded, session.sessionId],
  );

  useSession(session);

  const { configLoading, configError, configRoot } = useConfigFetch();
  const { country, countries, countryOpenSelectModal, onCountrySelectModalToggle, onConfirmCountry } = useCountry(
    configRoot?.operating_regions,
    configRoot?.operating_region_groups,
    configRoot?.defaultCountryCode,
    session.data.configuration_data,
    setSession,
  );

  // Display language follows the app UI language (set by the language selector → store → BE),
  // NOT the operating country. The selected country is where the site will operate, not which
  // language to render the configurator in.
  const trafficLocale = i18n.language;
  // Prices, however, display in the selected market's currency (e.g. MXN for MEX), overriding
  // whatever currency_code individual campaigns carry.
  const activeCurrency =
    country?.currency?.currency_code ?? country?.region_data?.currency_code ?? CONFIGURATOR_DEFAULT_CURRENCY;

  const { step, steps, isFirstStep, isLastStep, onPreviousStepClick, onNextStepClick, progress, onCancelPaymentClick } =
    useSteps(session.data.configuration_data, setSession);
  const { onEnterFullscreen, onExitFullscreen, showEnterFullscreen, showExitFullscreen } = useFullscreen(isMobile);
  const { breakpoint, breakpoints, modalBreakpoint, modalBreakpoints, openPreviewModal, onPreviewModalToggle } =
    useBreakpoints();
  const {
    designCategory,
    designCategories,
    designThemeOption,
    designThemeOptions,
    designThemeExpanded,
    onDesignThemeToggle,
    designColorsOption,
    designColorsOptions,
    designColorsExpanded,
    onDesignColorsToggle,
    designArtOption,
    designArtOptions,
    designArtExpanded,
    onDesignArtToggle,
    nextDesignCategory,
    previousDesignCategory,
    onDesignCategoryNext,
    onDesignCategoryPrevious,
    resetDesignCategory,
    setDesignCategoryToLast,
  } = useDesignStep(
    configRoot?.site_seed_groups,
    configRoot?.site_art_styles,
    session.data.configuration_data,
    setSession,
    step,
  );

  // Wrap step navigation so the Design step cycles its sub-categories
  // (Theme → Colors → Art Style) before advancing the header step.
  // Symmetric behavior on the way back; entering Design from Engagement
  // restores the sub-category to Art Style so the user lands where they left off.
  const onConfiguratorNextClick = React.useCallback(() => {
    // On mobile the Design step cycles its sub-categories (Theme → Colors → Art Style)
    // before advancing. On desktop all three are shown at once, so Continue goes straight
    // to the next step.
    if (isMobile && step.id === CONFIGURATOR_DESIGN_STEP.id && onDesignCategoryNext()) {
      return;
    }
    onNextStepClick();
  }, [isMobile, step.id, onDesignCategoryNext, onNextStepClick]);

  const onConfiguratorPreviousClick = React.useCallback(() => {
    if (isMobile && step.id === CONFIGURATOR_DESIGN_STEP.id && onDesignCategoryPrevious()) {
      return;
    }
    if (isMobile && step.id === CONFIGURATOR_ENGAGEMENT_STEP.id) {
      setDesignCategoryToLast();
    }
    onPreviousStepClick();
  }, [isMobile, step.id, onDesignCategoryPrevious, setDesignCategoryToLast, onPreviousStepClick]);
  // Fetch recommended campaigns on session load so they're available immediately
  // (engagement step, market scan quick-reveal) without waiting for the modal to open.
  React.useEffect(() => {
    if (!session.loaded || !session.sessionId) return;
    if (session.recommendedCampaigns.length > 0) return;
    dispatch(sessionFetchRecommendedCampaigns({ id: session.sessionId }));
  }, [session.loaded, session.sessionId, session.recommendedCampaigns.length, dispatch]);

  // Merge session.campaigns and session.recommendedCampaigns so useEngagementStep
  // can find the confirmed campaign regardless of which array it ended up in.
  const mergedCampaigns = React.useMemo(() => {
    const seen = new Set(session.campaigns.map((c) => String(c.id)));
    return [
      ...session.campaigns,
      ...session.recommendedCampaigns.filter((c) => !seen.has(String(c.id))),
    ];
  }, [session.campaigns, session.recommendedCampaigns]);

  const {
    engagementCampaign,
    engagementCampaigns,
    engagementItem,
    engagementItems,
    engagementHighlights,
    engagementHighlightsHeader,
    engagementVideos,
    engagementSelectedVideo,
    engagementHtmlPreviews,
    engagementSelectedHtml,
    engagementOpenSelectModal,
    onEngagementSelectModalToggle,
    marketScanPackName,
    engagementOffer,
    engagementIsCustomPack,
    engagementDrawerCampaignId,
    onEngagementDrawerOpen,
    onEngagementDrawerClose,
    engagementDrawerCampaign,
    engagementDrawerFeatures,
  } = useEngagementStep(
    configRoot?.gamification_items,
    configRoot?.gamification_tiers,
    mergedCampaigns,
    session.data.configuration_data,
    setSession,
    step,
    activeCurrency,
  );
  const msPlayedKey = (code?: string) => `ms_played_${code ?? ''}`;
  const [marketScanPlayed, setMarketScanPlayed] = React.useState(false);
  const onMarketScanPlayed = React.useCallback(() => {
    setMarketScanPlayed(true);
    try { if (country?.code) sessionStorage.setItem(msPlayedKey(country.code), '1'); } catch {}
  }, [country?.code]);

  // Hidden while a step-specific blocking phase is running.
  // Today that's only the market-scan animation on the Engagement step.
  const showNavigation = React.useMemo(
    () => step.id !== CONFIGURATOR_ENGAGEMENT_STEP.id || marketScanPlayed,
    [step.id, marketScanPlayed],
  );

  const prevCountryCodeRef = React.useRef(country?.code);
  React.useEffect(() => {
    const code = country?.code;

    // Restore played state from sessionStorage for the current country
    let played = false;
    try { played = !!code && sessionStorage.getItem(msPlayedKey(code)) === '1'; } catch {}
    setMarketScanPlayed(played);
    if (played) setMarketScanAnimationFinished();
    else resetMarketScanAnimation();

    // Only clear campaigns when the country actually changes (not on initial mount)
    if (prevCountryCodeRef.current === code) return;
    prevCountryCodeRef.current = code;
    dispatch(clearRecommendedCampaigns());
  }, [country?.code, dispatch]);

  const { trafficPlan, trafficPlans, trafficHighlights, trafficVideos } = useTrafficStep(
    configRoot?.traffic_options,
    country?.code,
    trafficLocale,
  );
  const { iframeLoading, iframeUrl } = useIframe(
    designThemeOption,
    designColorsOption,
    designArtOption,
    session.data.configuration_data,
    setSession,
  );

  const configuratorRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!engagementSelectedVideo || engagementSelectedVideo.loop) return;

    const container = configuratorRef.current;
    if (!container) return;

    const videos = container.querySelectorAll<HTMLVideoElement>('video');
    videos.forEach((video) => {
      if (video.ended || video.paused) {
        video.muted = true;
        video.currentTime = 0;
        video.play().catch(() => {});
      }
    });
  }, [engagementSelectedVideo]);

  const contextValue = React.useMemo<ConfiguratorContextValue>(
    () => ({
      configError,
      configLoading: configLoading || !session.loaded,
      isMobile: isMobile,
      isDesktop: !isMobile,
      price: splitPrices(session, activeCurrency),
      // Market currency (selected country's currency_code) — exposed so config
      // price nodes can label amounts consistently instead of guessing.
      activeCurrency,
      // Countries
      country,
      countries,
      countryOpenSelectModal,
      onCountrySelectModalToggle,
      onConfirmCountry,
      // Countries
      // Steps
      step,
      steps,
      isFirstStep,
      isLastStep,
      onPreviousStepClick: onConfiguratorPreviousClick,
      onNextStepClick: onConfiguratorNextClick,
      progress,
      onCancelPaymentClick,
      showNavigation,
      // Steps
      // Design Step
      designCategory,
      designCategories,
      designThemeOption,
      designThemeOptions,
      designThemeExpanded,
      onDesignThemeToggle,
      designColorsOption,
      designColorsOptions,
      designColorsExpanded,
      onDesignColorsToggle,
      designArtOption,
      designArtOptions,
      designArtExpanded,
      onDesignArtToggle,
      nextDesignCategory,
      previousDesignCategory,
      onDesignCategoryNext,
      onDesignCategoryPrevious,
      resetDesignCategory,
      setDesignCategoryToLast,
      // Design Step
      // Engagement Step
      engagementCampaign,
      engagementCampaigns,
      engagementItem,
      engagementItems,
      engagementHighlights,
      engagementHighlightsHeader,
      engagementVideos,
      engagementSelectedVideo,
      engagementHtmlPreviews,
      engagementSelectedHtml,
      engagementOpenSelectModal,
      onEngagementSelectModalToggle,
      hasRecommendedCampaign: !!session.data.configuration_data?.campaignId,
      marketScanPlayed,
      onMarketScanPlayed,
      marketScanPackName,
      engagementOffer,
      engagementIsCustomPack,
      engagementDrawerCampaignId,
      onEngagementDrawerOpen,
      onEngagementDrawerClose,
      engagementDrawerCampaign,
      engagementDrawerFeatures,
      // Engagement Step
      // Traffic Step
      trafficPlan,
      trafficPlans,
      trafficHighlights,
      trafficVideos,
      // Traffic Step
      // Payments Step
      paymentStatus: session.postPayment,
      // Payments Step
      onEnterFullscreen,
      onExitFullscreen,
      showEnterFullscreen,
      showExitFullscreen,
      // Breakpoints
      breakpoint,
      breakpoints,
      modalBreakpoint,
      modalBreakpoints,
      openPreviewModal,
      onPreviewModalToggle,
      // Breakpoints
      iframeLoading,
      iframeUrl,
    }),
    [
      session,
      activeCurrency,
      configError,
      configLoading,
      isMobile,
      // Countries
      country,
      countries,
      countryOpenSelectModal,
      onCountrySelectModalToggle,
      onConfirmCountry,
      // Countries
      // Steps
      step,
      steps,
      isFirstStep,
      isLastStep,
      onConfiguratorPreviousClick,
      onConfiguratorNextClick,
      progress,
      onCancelPaymentClick,
      showNavigation,
      // Steps
      // Design Step
      designCategory,
      designCategories,
      designThemeOption,
      designThemeOptions,
      designThemeExpanded,
      onDesignThemeToggle,
      designColorsOption,
      designColorsOptions,
      designColorsExpanded,
      onDesignColorsToggle,
      designArtOption,
      designArtOptions,
      designArtExpanded,
      onDesignArtToggle,
      nextDesignCategory,
      previousDesignCategory,
      onDesignCategoryNext,
      onDesignCategoryPrevious,
      resetDesignCategory,
      setDesignCategoryToLast,
      // Design Step
      // Engagement Step
      engagementCampaign,
      engagementCampaigns,
      engagementItem,
      engagementItems,
      engagementHighlights,
      engagementHighlightsHeader,
      engagementVideos,
      engagementSelectedVideo,
      engagementHtmlPreviews,
      engagementSelectedHtml,
      engagementOpenSelectModal,
      onEngagementSelectModalToggle,
      marketScanPlayed,
      onMarketScanPlayed,
      marketScanPackName,
      engagementOffer,
      engagementIsCustomPack,
      engagementDrawerCampaignId,
      onEngagementDrawerOpen,
      onEngagementDrawerClose,
      engagementDrawerCampaign,
      engagementDrawerFeatures,
      // Engagement Step
      // Traffic Step
      trafficPlan,
      trafficPlans,
      trafficHighlights,
      trafficVideos,
      // Traffic Step
      onEnterFullscreen,
      onExitFullscreen,
      showEnterFullscreen,
      showExitFullscreen,
      // Breakpoints
      breakpoint,
      breakpoints,
      modalBreakpoint,
      modalBreakpoints,
      openPreviewModal,
      onPreviewModalToggle,
      // Breakpoints
      iframeLoading,
      iframeUrl,
    ],
  );

  console.log('Configurator[contextValue]', contextValue);

  if (configLoading || !session.loaded) {
    return <PageLoader />;
  }

  return (
    <ModuleElementDiv ref={configuratorRef} className={componentProps.className} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default SparkConfigurator;
